/**
 * Componente de navegación inferior para la versión web
 * Replicación de la versión móvil (CustomTabbar)
 * Muestra los 4 botones principales: Chat, Radar, Alertas, Perfil
 * Incluye contadores de interacciones en tiempo real
 */

import React, { memo, useCallback, useRef, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useInteractionCounter } from '../../hooks/useInteractionCounter';
import { useResetCountersMutation } from '../../hooks/useInteractions';
import { useInteractionCounterRealtime } from '../../hooks/useInteractionCounterRealtime';

// Importar componentes de botones individuales
import ChatButton from './NavbarBottom/ChatButton';
import RadarButton from './NavbarBottom/RadarButton';
import AlertsButton from './NavbarBottom/AlertsButton';
import ProfileButton from './NavbarBottom/ProfileButton';

// Constante para controlar logs de desarrollo
const DEBUG = process.env.NODE_ENV === 'development';

const NavbarBottom = () => {
  const { session } = useAuth();
  
  // Memoizar userId para evitar re-renders cuando session cambia pero user.id es el mismo
  const userId = useMemo(() => session?.user?.id, [session?.user?.id]);
  
  // Ref para trackear valores anteriores y detectar cambios
  const previousValuesRef = useRef({
    interactionData: null,
    isResetting: false,
    userId: null,
  });
  
  // Usar hook personalizado para obtener datos del contador de interacciones
  const { data: interactionData = { visitas: 0, me_gustas: 0, mensajes: 0 }, isLoading: loading } = useInteractionCounter(session);
  
  // Hook para reiniciar contadores de interacciones
  const { mutate: resetCounters, isPending: isResetting } = useResetCountersMutation(userId);
  
  // Estado optimista: si se está reseteando, mostrar como si ya estuviera en 0
  // Memoizado para evitar re-renders cuando isResetting cambia pero interactionData es el mismo
  const optimisticInteractionData = useMemo(() => {
    return isResetting ? 
      { ...interactionData, visitas: 0, me_gustas: 0 } : 
      interactionData;
  }, [interactionData, isResetting]);
  
  // Detectar qué cambió
  const currentValues = {
    interactionData,
    isResetting,
    userId,
  };
  
  const changedKeys = {};
  for (const key in currentValues) {
    if (JSON.stringify(previousValuesRef.current[key]) !== JSON.stringify(currentValues[key])) {
      changedKeys[key] = {
        old: previousValuesRef.current[key],
        new: currentValues[key],
      };
    }
  }
  
  if (Object.keys(changedKeys).length > 0) {
    console.log('[NavbarBottom] Valores que cambiaron:', Object.keys(changedKeys));
    console.log('[NavbarBottom] Cambios:', changedKeys);
  }
  
  previousValuesRef.current = currentValues;
  
  /**
   * Callbacks para manejar cambios en tiempo real en los contadores
   * Memoizados para evitar re-creación en cada render
   */
  const handleVisitasChange = useCallback((nuevoValor, valorAnterior) => {
    if (DEBUG) console.log(`[NavbarBottom] Cambio en visitas: ${valorAnterior} → ${nuevoValor}`);
  }, []);
  
  const handleMeGustasChange = useCallback((nuevoValor, valorAnterior) => {
    if (DEBUG) console.log(`[NavbarBottom] Cambio en me gustas: ${valorAnterior} → ${nuevoValor}`);
  }, []);
  
  const handleMensajesChange = useCallback((nuevoValor, valorAnterior) => {
    if (DEBUG) console.log(`[NavbarBottom] Cambio en mensajes: ${valorAnterior} → ${nuevoValor}`);
  }, []);
  
  const handleCounterChange = useCallback((cambios, newData, oldData) => {
    if (DEBUG) console.log('[NavbarBottom] Cambio en interaction_counter:', cambios);
    console.log('[NavbarBottom] Cambio en interaction_counter:', cambios);
  }, []);
  
  /**
   * Maneja el reinicio de contadores cuando se navega a alertas
   * Memoizado para evitar re-creación en cada render
   */
  const handleResetCounters = useCallback(() => {
    if (optimisticInteractionData.visitas > 0 || optimisticInteractionData.me_gustas > 0) {
      if (DEBUG) console.log('[NavbarBottom] Reiniciando contadores');
      resetCounters();
    }
  }, [optimisticInteractionData.visitas, optimisticInteractionData.me_gustas, resetCounters]);
  
  /**
   * Usar hook específico para suscripciones en tiempo real a interaction_counter
   * Escucha cambios en las columnas del contador de interacciones del usuario actual
   */
  useInteractionCounterRealtime({
    onVisitasChange: handleVisitasChange,
    onMeGustasChange: handleMeGustasChange,
    onMensajesChange: handleMensajesChange,
    onCounterChange: handleCounterChange
  });
  
  // No renderizar nada si está cargando
  if (loading) {
    if (DEBUG) console.log('[NavbarBottom] No renderizando (loading)');
    return null;
  }
  
  if (DEBUG) console.log('[NavbarBottom] Renderizando componente con interactionData:', interactionData);
  
  return (
    <footer 
      className="sticky bottom-0 w-full flex items-center justify-center bg-neutral-900 border-t border-neutral-800 pb-2"
    >
      {/* Contenedor principal con estilo de pill */}
      <div 
        className="flex items-center justify-center gap-2 p-2 rounded-full"
      >
        {/* CHAT - muestra contador de mensajes */}
        <ChatButton
          messageCount={optimisticInteractionData.mensajes}
        />
        
        {/* RADAR - no muestra contador */}
        <RadarButton />
        
        {/* ALERTAS - muestra contador de interacciones (visitas + me_gustas) */}
        <AlertsButton
          interactionCount={optimisticInteractionData.visitas + optimisticInteractionData.me_gustas}
          onReset={handleResetCounters}
        />
        
        {/* PERFIL - no muestra contador */}
        <ProfileButton />
      </div>
    </footer>
  );
};

// Memoizar el componente para evitar re-renders innecesarios
// Solo se re-renderiza si interactionData cambia realmente
export default memo(NavbarBottom, (prevProps, nextProps) => {
  // NavbarBottom no recibe props, así que siempre debería ser el mismo
  // El memo aquí es para prevenir re-renders desde el padre
  return true;
});